import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, Search, Plus } from "lucide-react";
import config from "../../utils/config";
import RecipeGrid from "../../components/RecipeGrid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown, faTemperatureEmpty, faCarrot, faEgg, faPepperHot, faLeaf, faDrumstickBite, faPaperclip, faMagicWandSparkles } from "@fortawesome/free-solid-svg-icons";
import { Player } from '@lottiefiles/react-lottie-player';
import loader from '../../loader/main_loading_animation.json';
import toast from 'react-hot-toast';

const IngredientSearch = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [ingredients, setIngredients] = useState([]);
  const [currentIngredient, setCurrentIngredient] = useState("");
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [totalRecipes, setTotalRecipes] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [showAnalysisAnimation, setShowAnalysisAnimation] = useState(false);

  useEffect(() => {
    // Parse URL query parameters for ingredients
    const queryParams = new URLSearchParams(location.search);
    const ingredientsParam = queryParams.get('ingredients');
    
    if (ingredientsParam) {
      // Split by comma and process each ingredient
      const ingredientList = ingredientsParam.split(',');
      const processedIngredients = ingredientList.map(item => item.trim().toLowerCase()).filter(item => item !== "");
      
      if (processedIngredients.length > 0) {
        setIngredients(processedIngredients);
        setHasSearched(true);
        // Search with these ingredients instead of loading trending recipes
        searchRecipesWithIngredients(processedIngredients);
        return;
      }
    }
    
    // If no ingredients in URL, load trending recipes
    if (!hasSearched) {
      loadTrendingRecipes();
    }
  }, [location.search]);

  const loadTrendingRecipes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.API_SERVER_URL}/api/v3/trending-recipes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page,
          perPage
        }),
      });
      const data = await response.json();
      setRecipes(data.recipes || []);
      setTotalRecipes(data.total || 0);
    } catch (error) {
      console.error("Error loading trending recipes:", error);
    } finally {
      setLoading(false);
    }
  };

  const searchRecipesWithIngredients = async (ingredientsList) => {
    setLoading(true);
    
    try {
      const response = await fetch(`${config.API_SERVER_URL}/api/v3/search-by-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ingredients: ingredientsList,
          page,
          perPage
        }),
      });
      
      const data = await response.json();
      if (data.recipes && data.recipes.length > 0) {
        setRecipes(data.recipes);
        setTotalRecipes(data.total || 0);
      } else {
        await loadTrendingRecipes();
      }
    } catch (error) {
      console.error("Error searching recipes:", error);
      await loadTrendingRecipes();
    } finally {
      setLoading(false);
    }
  };

  const searchRecipes = async () => {
    if (ingredients.length === 0) {
      loadTrendingRecipes();
      return;
    }

    setHasSearched(true);
    searchRecipesWithIngredients(ingredients);
  };

  const addIngredient = () => {
    if (currentIngredient.trim() !== "") {
      // Split by comma and process each ingredient
      const ingredientList = currentIngredient.split(',');
      
      let newIngredients = [...ingredients];
      
      ingredientList.forEach(item => {
        const trimmedItem = item.trim().toLowerCase();
        if (trimmedItem !== "" && !newIngredients.includes(trimmedItem)) {
          newIngredients.push(trimmedItem);
        }
      });
      
      setIngredients(newIngredients);
      setCurrentIngredient("");
    }
  };
  
  const removeIngredient = (index) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients.splice(index, 1);
    setIngredients(updatedIngredients);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addIngredient();
    }
  };

  const handleSearch = () => {
    setPage(1);
    searchRecipes();
  };

  const changePage = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
    searchRecipes();
  };

  // Map icons to some ingredients for visual appeal
  const ingredientIcons = {
    "chicken": faDrumstickBite,
    "tomato": faPepperHot,
    "onion": faCarrot,
    "spinach": faLeaf,
    "egg": faEgg,
  };

  const suggestionIngredients = [
    "chicken", "tomato", "onion", "garlic", "pasta", 
    "beef", "rice", "potato", "carrot", "bell pepper",
    "cheese", "egg", "spinach", "broccoli", "mushroom", 
    "basil", "lemon", "olive oil", "butter", "milk"
  ];

  const visibleSuggestions = showAllSuggestions 
    ? suggestionIngredients 
    : suggestionIngredients.slice(0, 10);

  const handleTagClick = (ingredient) => {
    if (!ingredients.includes(ingredient)) {
      setIngredients([...ingredients, ingredient]);
    }
  };

  // Enhanced image compression function with progressive quality reduction
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Start with aggressive compression for very large images
        let maxSize = 800;
        let quality = 0.7;
        
        // If original image is very large, be more aggressive
        if (img.width > 2000 || img.height > 2000) {
          maxSize = 600;
          quality = 0.5;
        }
        if (img.width > 4000 || img.height > 4000) {
          maxSize = 400;
          quality = 0.3;
        }
        
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Progressive compression - keep reducing quality until size is acceptable
        const tryCompress = (currentQuality) => {
          const compressedDataUrl = canvas.toDataURL('image/jpeg', currentQuality);
          
          // Check if compressed size is acceptable (aim for under 2MB base64)
          const sizeInBytes = (compressedDataUrl.length * 3) / 4;
          const sizeInMB = sizeInBytes / (1024 * 1024);
          
          if (sizeInMB > 2 && currentQuality > 0.1) {
            // Still too large, reduce quality further
            return tryCompress(currentQuality - 0.1);
          } else {
            // Size is acceptable or we've reached minimum quality
            resolve(compressedDataUrl);
          }
        };
        
        tryCompress(quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle file input change
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Show processing message for large files
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 5) {
      toast.loading('Processing large image, this may take a moment...', { duration: 3000 });
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(file);
      setImagePreview(e.target.result);
      if (fileSizeInMB > 5) {
        toast.success('Large image processed successfully!');
      }
    };
    reader.readAsDataURL(file);
  };

  // Remove uploaded image
  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
  };

  // Extract ingredient name from full description
  const extractIngredientName = (ingredientLine) => {
    // Remove common quantity patterns and get the main ingredient
    const cleaned = ingredientLine
      .replace(/"/g, '') // Remove all quotes first
      .replace(/^\d+(\.\d+)?\s*(cups?|tablespoons?|teaspoons?|tbsp|tsp|oz|ounces?|pounds?|lbs?|grams?|kg|ml|liters?)\s*/i, '')
      .replace(/^(a|an|the)\s+/i, '')
      .replace(/,.*$/, '') // Remove everything after comma
      .replace(/\(.*\)/, '') // Remove parentheses content
      .trim();
    
    return cleaned;
  };

  // Handle image analysis
  const handleAnalyzeImage = async () => {
    if (!uploadedImage) return;
    
    setIsAnalyzingImage(true);
    setShowAnalysisAnimation(true);
    
    try {
      // Compress image before sending
      const compressedImage = await compressImage(uploadedImage);
      
      const response = await fetch(`${config.API_SERVER_URL}/api/v3/extract-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          base64Image: compressedImage
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      // Check for error response (improved error handling)
      if (!data.success || data.error) {
        console.error('API Error:', data.error);
        
        // Check for various error message formats
        const errorMessage = data.error || 'Unknown error';
        const isNotRecipeError = errorMessage.toLowerCase().includes('not a recipe') || 
                                errorMessage.toLowerCase().includes('not suitable') ||
                                errorMessage.toLowerCase().includes('no valid ingredients');
        
        if (isNotRecipeError) {
          alert('This image doesn\'t appear to contain recipe ingredients. Please upload an image of food ingredients or a recipe.');
        } else {
          alert(`Error: ${errorMessage}`);
        }
        
        setIsAnalyzingImage(false);
        setShowAnalysisAnimation(false);
        return;
      }
      
      // Extract ingredients from API response
      if (data.ingredients && Array.isArray(data.ingredients) && data.ingredients.length > 0) {
        const newIngredients = [...ingredients];
        
        // Process the clean ingredients from the improved backend
        data.ingredients.forEach(ingredientName => {
          // Remove any remaining quotes and clean the ingredient name
          const cleanIngredient = ingredientName
            .replace(/"/g, '') // Remove any quotes
            .trim()
            .toLowerCase();
          
          if (cleanIngredient && !newIngredients.includes(cleanIngredient)) {
            newIngredients.push(cleanIngredient);
          }
        });
        
        // Show success animation for 2 seconds then navigate
        setTimeout(() => {
          setIsAnalyzingImage(false);
          setShowAnalysisAnimation(false);
          
          // Navigate to search results with ingredients
          const queryParams = newIngredients.length > 0
            ? `?ingredients=${newIngredients.join(',')}`
            : "";
          
          navigate(`/search${queryParams}`);
        }, 2000);
        
        console.log('Processed ingredients:', newIngredients.filter(ing => !ingredients.includes(ing)));
        
      } else {
        console.warn('No ingredients found in API response');
        alert('No ingredients could be identified in this image. Please try a different image.');
        setIsAnalyzingImage(false);
        setShowAnalysisAnimation(false);
      }
      
    } catch (error) {
      console.error('Error analyzing image:', error);
      if (error.message.includes('413') || error.message.includes('Payload Too Large')) {
        alert('Image is too large. Please try a smaller image or compress it first.');
      } else {
        alert('Failed to analyze image. Please check your connection and try again.');
      }
      setIsAnalyzingImage(false);
      setShowAnalysisAnimation(false);
    }
  };

  // Helper function to get random pastel color for ingredient buttons
  const getIngredientColor = (ingredient) => {
    const colors = [
      'bg-[#e8f5ec] text-[#0e4621]', // Green
      'bg-[#f0f5ff] text-[#1e40af]', // Blue
      'bg-[#fff4e6] text-[#c05621]', // Orange
      'bg-[#faf5ff] text-[#6b46c1]', // Purple
      'bg-[#f0ffff] text-[#047481]'  // Teal
    ];
    
    // Use string hashing to assign consistent colors to ingredients
    let hash = 0;
    for (let i = 0; i < ingredient.length; i++) {
      hash = ingredient.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <>
      {loading ? (
        <div style={{ minWidth: '100px', textAlign: 'center', verticalAlign: 'middle', alignItems: 'center', justifyContent: 'center', height: '400px', display: 'flex', flexDirection: 'column' }}>
          <Player
            src={loader}
            className="player w-[40%]"
            loop
            autoplay
          />
        </div>
      ) : (
        <div className="content-section mx-auto mt-5">
          <div className="px-4 md:px-0">
            <h1 className="text-2xl mb-3 font-bold text-[#0e4621] mt-5">What's in your kitchen?</h1>
            <p className="text-gray-600 mb-5">Search for recipes using ingredients you already have</p>
            
            {/* Redesigned Search Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-8">
              {/* Input Section - Streamlined */}
              <div className="p-4">
                <div className="relative mb-4">
                  <input
                    type="text"
                    value={currentIngredient}
                    onChange={(e) => setCurrentIngredient(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter an ingredient (e.g., chicken, tomato)"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-md focus:outline-none focus:border-[#0e4621] transition-colors pr-20"
                  />
                  
                  {/* Image Upload - Inside Input */}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="image-upload-ingredient"
                  />
                  <label
                    htmlFor="image-upload-ingredient"
                    className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#0e4621] p-2 transition duration-300 flex items-center justify-center cursor-pointer"
                    title="Upload image to scan for ingredients"
                  >
                    <FontAwesomeIcon icon={faPaperclip} size="sm" />
                  </label>
                  
                  {/* Plus Button - Inside Input */}
                  <button
                    onClick={addIngredient}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#0e4621] hover:text-[#0a3419] p-2 transition duration-300 flex items-center justify-center"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                
                {/* Image Preview */}
                {imagePreview && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-start space-x-4">
                      <img 
                        src={imagePreview} 
                        alt="Uploaded" 
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-2">Image uploaded successfully!</p>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleAnalyzeImage}
                            disabled={isAnalyzingImage}
                            className="px-4 py-2 bg-[#0e4621] text-white text-sm rounded-lg hover:bg-[#0a3419] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isAnalyzingImage ? 'Analyzing...' : '🔍 Scan for ingredients'}
                          </button>
                          <button
                            onClick={removeImage}
                            className="px-4 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Ingredients Tags - Compact and Styled */}
                {ingredients.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {ingredients.map((ingredient, index) => (
                        <div key={index} className="bg-[#0e4621] text-white px-3 py-1.5 rounded-full flex items-center text-sm shadow-sm">
                          <span>{ingredient}</span>
                          <button 
                            onClick={() => removeIngredient(index)}
                            className="ml-2 text-white hover:text-red-200 focus:outline-none"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Popular Ingredients Section - More Visually Appealing */}
              <div className="bg-[#f7f9f8] border-t border-b border-gray-100 py-3 px-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-[#0e4621] font-medium">Popular ingredients</p>
                  <button 
                    onClick={() => setShowAllSuggestions(!showAllSuggestions)}
                    className="text-[#0e4621] text-sm flex items-center hover:underline"
                  >
                    {showAllSuggestions ? "Show less" : "Show more"}
                    <FontAwesomeIcon 
                      className="ml-1" 
                      icon={showAllSuggestions ? faChevronUp : faChevronDown} 
                    />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {visibleSuggestions.map((ingredient) => {
                    const colorClass = getIngredientColor(ingredient);
                    return (
                      <button
                        key={ingredient}
                        onClick={() => handleTagClick(ingredient)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:shadow-md ${
                          ingredients.includes(ingredient)
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed opacity-60"
                            : `${colorClass} hover:opacity-90`
                        } flex items-center`}
                        disabled={ingredients.includes(ingredient)}
                      >
                        {ingredientIcons[ingredient] && (
                          <FontAwesomeIcon 
                            icon={ingredientIcons[ingredient]} 
                            className="mr-1.5" 
                          />
                        )}
                        {ingredient}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Search Button - Compact Footer */}
              <div className="px-4 py-3">
                <button
                  onClick={handleSearch}
                  className="w-full bg-[#0e4621] hover:bg-[#0a3419] text-white py-2.5 rounded-md transition duration-300 flex items-center justify-center shadow-sm hover:shadow"
                >
                  <Search size={16} className="mr-2" />
                  <span className="font-medium">Find Recipes</span>
                </button>
              </div>
            </div>
            
            {/* Results Section */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center md:mt-6">
              <div>
                <h1 className="text-2xl mb-3 font-bold text-[#0e4621]">
                  {hasSearched && ingredients.length > 0 
                    ? `Recipes with ${ingredients.join(', ')}`
                    : "Trending Recipes"}
                </h1>
              </div>
            </div>
            
            {recipes.length > 0 ? (
              <div className="p-4 md:p-0">
                <RecipeGrid 
                  recipes={recipes} 
                  type='consumer' 
                  user={props.user} 
                  tenant={props.tenant} 
                  handleTagClick={handleTagClick} 
                />
              </div>
            ) : (
              <div className="text-center p-12">
                <p className="text-center p-3">
                  <FontAwesomeIcon icon={faTemperatureEmpty} style={{ paddingRight: '12px' }} />
                  No recipes found
                </p>
              </div>
            )}
            
            {/* Pagination */}
            {totalRecipes > perPage && (
              <div className="flex justify-center mt-8 mb-12">
                <div className="flex space-x-2">
                  <button
                    onClick={() => changePage(page - 1)}
                    disabled={page === 1}
                    className={`px-4 py-2 rounded-md transition ${
                      page === 1 
                        ? "bg-gray-100 text-gray-400" 
                        : "bg-[#e8f5ec] text-[#0e4621] hover:bg-[#d0ebda]"
                    }`}
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center px-4">
                    <span className="text-gray-700">
                      Page {page} of {Math.ceil(totalRecipes / perPage)}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => changePage(page + 1)}
                    disabled={page >= Math.ceil(totalRecipes / perPage)}
                    className={`px-4 py-2 rounded-md transition ${
                      page >= Math.ceil(totalRecipes / perPage) 
                        ? "bg-gray-100 text-gray-400" 
                        : "bg-[#e8f5ec] text-[#0e4621] hover:bg-[#d0ebda]"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Analysis Animation Overlay */}
      {showAnalysisAnimation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center">
            <Player
              autoplay
              loop
              src={loader}
              style={{ height: '150px', width: '150px' }}
            />
            <div className="mt-4">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <FontAwesomeIcon icon={faMagicWandSparkles} className="text-[#0e4621] text-xl" />
                <h3 className="text-lg font-semibold text-gray-800">Analyzing Image</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Scanning your image for ingredients...
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IngredientSearch;